import { useEffect, useState } from "react";

/**
 * Store links (SPR-021 D1). Built ahead of the public app release.
 */
export const APP_STORE_URL = "https://apps.apple.com/app/id6783500243";
export const PLAY_STORE_URL = "https://play.google.com/store/apps/details?id=com.tcsb";

/**
 * detectPlatform — Classify the platform from a userAgent string.
 * @param {string} ua - typically navigator.userAgent
 * @returns {"ios"|"android"|"other"}
 */
export function detectPlatform(ua = "") {
  const s = ua.toLowerCase();
  // iPadOS 13+ reports as "Macintosh"; treat touch-capable Macs as iPad too.
  const isIOS =
    /iphone|ipad|ipod/.test(s) ||
    (s.includes("macintosh") &&
      typeof navigator !== "undefined" &&
      navigator.maxTouchPoints > 1);
  if (isIOS) return "ios";
  if (s.includes("android")) return "android";
  return "other";
}

/**
 * AppRedirect — Hidden store-redirect page (SPR-021 A-02), served at /app.
 *
 * On mount, detects the platform from navigator.userAgent and redirects iOS to
 * the App Store and Android to the Play Store. On any other platform (desktop),
 * it renders a branded chooser with both store buttons. Not linked from any
 * nav/footer — shared via the admin QR code (A-03).
 */
export default function AppRedirect() {
  // Resolve only once, after mount, so SSR/test environments behave predictably.
  const [platform, setPlatform] = useState("other");

  useEffect(() => {
    const previous = document.title;
    document.title = "Get the App — Twin City Silver Bullet";

    const ua = typeof navigator !== "undefined" ? navigator.userAgent : "";
    const detected = detectPlatform(ua);
    setPlatform(detected);

    if (detected === "ios") {
      window.location.replace(APP_STORE_URL);
    } else if (detected === "android") {
      window.location.replace(PLAY_STORE_URL);
    }

    return () => {
      document.title = previous;
    };
  }, []);

  // While a mobile redirect is in flight, show a minimal "Redirecting…" message.
  const redirecting = platform === "ios" || platform === "android";

  return (
    <main
      data-testid="app-redirect-page"
      className="min-h-screen bg-cream text-ink font-body flex items-center justify-center px-4 py-16"
    >
      <section className="max-w-md w-full text-center">
        <img
          src="/app-icon-512.png"
          alt="Twin City Silver Bullet app icon"
          className="w-24 h-24 mx-auto rounded-2xl border-2 border-charcoal shadow-[4px_4px_0_rgba(43,39,36,0.18)]"
        />
        <h1 className="font-display text-4xl text-charcoal uppercase mt-6">
          Get the App
        </h1>

        {redirecting ? (
          <p
            className="font-body text-ink/70 mt-4"
            data-testid="app-redirect-redirecting"
          >
            Redirecting you to the store…
          </p>
        ) : (
          <>
            <p className="font-body text-ink/70 mt-4 mb-8">
              Download the Twin City Silver Bullet app for your phone.
            </p>
            <div className="flex flex-col gap-4" data-testid="app-redirect-buttons">
              <a
                href={APP_STORE_URL}
                data-testid="app-store-link"
                className="bg-charcoal text-white font-heading tracking-widest py-3 rounded-xl shadow-[0_4px_0_rgba(43,39,36,0.4)] hover:translate-y-0.5 transition-all"
              >
                DOWNLOAD ON THE APP STORE
              </a>
              <a
                href={PLAY_STORE_URL}
                data-testid="play-store-link"
                className="bg-rust text-white font-heading tracking-widest py-3 rounded-xl shadow-[0_4px_0_#7f371e] hover:translate-y-0.5 transition-all"
              >
                GET IT ON GOOGLE PLAY
              </a>
            </div>
          </>
        )}
      </section>
    </main>
  );
}
